import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import Lenis from 'lenis';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import Navbar from './components/Layout/Navbar';
import Footer from './components/Layout/Footer';
import HomePage from './pages/HomePage';
import ShopPage from './pages/ShopPage';
import ProductDetailPage from './pages/ProductDetailPage';
import CartPage from './pages/CartPage';
import CheckoutPage from './pages/CheckoutPage';
import CheckoutSuccessPage from './pages/CheckoutSuccessPage';
import LoginPage from './pages/Auth/LoginPage';
import RegisterPage from './pages/Auth/RegisterPage';
import AccountPage from './pages/Account/AccountPage';
import OrdersPage from './pages/Account/OrdersPage';
import OrderDetailPage from './pages/Account/OrderDetailPage';
import WishlistPage from './pages/Account/WishlistPage';
import AddressesPage from './pages/Account/AddressesPage';
import AboutPage from './pages/AboutPage';
import BirthstonesPage from './pages/BirthstonesPage';
import RequestStonePage from './pages/RequestStonePage';
import BlogPage from './pages/BlogPage';
import BlogPostPage from './pages/BlogPostPage';
import ContactPage from './pages/ContactPage';
import TrackOrderPage from './pages/TrackOrderPage';
import AdminDashboard from './pages/Admin/AdminDashboard';
import AdminProducts from './pages/Admin/AdminProducts';
import AdminOrders from './pages/Admin/AdminOrders';
import AdminCustomers from './pages/Admin/AdminCustomers';
import AdminMessages from './pages/Admin/AdminMessages';
import AdminBlog from './pages/Admin/AdminBlog';
import AdminStoneRequests from './pages/Admin/AdminStoneRequests';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import './App.css';

// Smooth scroll setup
function SmoothScroll() {
  useEffect(() => {
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
    };
  }, []);

  return null;
}

// Protected route wrapper
function ProtectedRoute({ children, adminOnly = false }) {
  const { isAuthenticated, isAdmin, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
      </div>
    );
  }

  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && !isAdmin()) {
    return <Navigate to="/" replace />;
  }

  return children;
}

// Layout wrapper
function Layout({ children }) {
  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <main className="flex-grow pt-20">{children}</main>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <SmoothScroll />
            <Toaster position="top-center" />
            <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Layout><HomePage /></Layout>} />
            <Route path="/shop" element={<Layout><ShopPage /></Layout>} />
            <Route path="/products/:id" element={<Layout><ProductDetailPage /></Layout>} />
            <Route path="/about" element={<Layout><AboutPage /></Layout>} />
            <Route path="/birthstones" element={<Layout><BirthstonesPage /></Layout>} />
            <Route path="/request-stone" element={<Layout><RequestStonePage /></Layout>} />
            <Route path="/blog" element={<Layout><BlogPage /></Layout>} />
            <Route path="/blog/:slug" element={<Layout><BlogPostPage /></Layout>} />
            <Route path="/contact" element={<Layout><ContactPage /></Layout>} />
            <Route path="/track-order" element={<Layout><TrackOrderPage /></Layout>} />
            <Route path="/privacy" element={<Layout><PrivacyPage /></Layout>} />
            <Route path="/terms" element={<Layout><TermsPage /></Layout>} />
            
            {/* Auth Routes */}
            <Route path="/login" element={<Layout><LoginPage /></Layout>} />
            <Route path="/register" element={<Layout><RegisterPage /></Layout>} />
            
            {/* Protected Customer Routes */}
            <Route path="/cart" element={<Layout><ProtectedRoute><CartPage /></ProtectedRoute></Layout>} />
            <Route path="/checkout" element={<Layout><ProtectedRoute><CheckoutPage /></ProtectedRoute></Layout>} />
            <Route path="/checkout/success" element={<Layout><ProtectedRoute><CheckoutSuccessPage /></ProtectedRoute></Layout>} />
            <Route path="/account" element={<Layout><ProtectedRoute><AccountPage /></ProtectedRoute></Layout>} />
            <Route path="/orders" element={<Layout><ProtectedRoute><OrdersPage /></ProtectedRoute></Layout>} />
            <Route path="/orders/:id" element={<Layout><ProtectedRoute><OrderDetailPage /></ProtectedRoute></Layout>} />
            <Route path="/wishlist" element={<Layout><ProtectedRoute><WishlistPage /></ProtectedRoute></Layout>} />
            <Route path="/addresses" element={<Layout><ProtectedRoute><AddressesPage /></ProtectedRoute></Layout>} />
            
            {/* Protected Admin Routes */}
            <Route path="/admin" element={<Layout><ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute></Layout>} />
            <Route path="/admin/products" element={<Layout><ProtectedRoute adminOnly><AdminProducts /></ProtectedRoute></Layout>} />
            <Route path="/admin/orders" element={<Layout><ProtectedRoute adminOnly><AdminOrders /></ProtectedRoute></Layout>} />
            <Route path="/admin/customers" element={<Layout><ProtectedRoute adminOnly><AdminCustomers /></ProtectedRoute></Layout>} />
            <Route path="/admin/messages" element={<Layout><ProtectedRoute adminOnly><AdminMessages /></ProtectedRoute></Layout>} />
            <Route path="/admin/blog" element={<Layout><ProtectedRoute adminOnly><AdminBlog /></ProtectedRoute></Layout>} />
            <Route path="/admin/stone-requests" element={<Layout><ProtectedRoute adminOnly><AdminStoneRequests /></ProtectedRoute></Layout>} />
            
            {/* 404 */}
            <Route path="*" element={<Layout><div className="min-h-screen flex items-center justify-center"><h1 className="text-4xl font-serif">Page Not Found</h1></div></Layout>} />
          </Routes>
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
